import { clean, money, num, stripHtml } from './format';

export interface FeeFacts {
  appFee: string | null;
  adminFee: string | null;
  /** Security deposit value and its note: ["Varies", "with screening"] or ["$500", ""]. */
  deposit: [string, string] | null;
  depositAlt: boolean;
  /** "$232.94–$252.94" summed from the required monthly charges. */
  monthlyRange: string | null;
  /** The required monthly charges as written, plus "Renters insurance required." when the field says so. */
  monthlyText: string | null;
  /** The whole field, when it could not be read into parts. */
  raw: string | null;
}

const cents = (n: number) => (Number.isInteger(n) ? money(n)! : `$${n.toFixed(2)}`);

/**
 * property_fees_deposit_info is free text, often written like:
 * "Security deposit varies with application screening; a security deposit alternative is available.
 *  Required monthly charges: utility billing $11.99, trash $10, …; renters insurance required."
 * Anything it can't read falls back to the field as written.
 */
export function feeFacts(p: Record<string, unknown>): FeeFacts {
  const info = stripHtml(clean(p.property_fees_deposit_info) || '');
  const app = num(clean(p.property_application_fee));
  const admin = num(clean(p.property_admin_fee));

  let deposit: [string, string] | null = null;
  if (/security deposit varies/i.test(info)) deposit = ['Varies', /screening/i.test(info) ? 'with screening' : ''];
  else {
    const m = /security deposit[^.;$]*?\$([\d,]+(?:\.\d+)?)/i.exec(info);
    if (m) deposit = [cents(num(m[1])!), ''];
  }

  let monthlyRange: string | null = null;
  let monthlyText: string | null = null;
  const m = /required monthly charges:\s*([\s\S]+?)(?:;|\.(?=\s|$))/i.exec(info);
  if (m) {
    let lo = 0;
    let hi = 0;
    m[1].split(/,\s*/).forEach((item) => {
      const amounts = [...item.matchAll(/\$([\d,]+(?:\.\d+)?)/g)].map((x) => num(x[1])!).filter((x) => x != null);
      if (!amounts.length) return;
      lo += Math.min(...amounts);
      hi += Math.max(...amounts);
    });
    if (lo) monthlyRange = hi > lo + 0.001 ? `${cents(lo)}–${cents(hi)}` : cents(lo);
    const text = m[1].trim();
    monthlyText = `${text[0].toUpperCase()}${text.slice(1)}.${/renters insurance required/i.test(info) ? ' Renters insurance required.' : ''}`;
  }

  return {
    appFee: app ? money(app) : null,
    adminFee: admin ? money(admin) : null,
    deposit,
    depositAlt: /deposit alternative[^.;]*available/i.test(info),
    monthlyRange,
    monthlyText,
    raw: info && !m && !deposit ? info : null,
  };
}

export interface FeeLine {
  name: string;
  amount: string;
  applied: string;
  freq: string;
  note?: string;
  /** Lowest and highest amount, for totals. */
  lo?: number;
  hi?: number;
}

export const hasFees = (f: FeeFacts) => !!(f.appFee || f.adminFee || f.deposit || f.monthlyRange || f.raw);

/** The four pet cells shared by the amenities and floor plans pages: [label, value, note]. */
export function petCells(p: Record<string, unknown>): Array<[string, string, string]> {
  const kinds = [p.property_allows_dogs && 'Dogs', p.property_allows_cats && 'cats'].filter(Boolean) as string[];
  const welcome = kinds.length === 2 ? 'Dogs & cats' : kinds[0] ? kinds[0][0].toUpperCase() + kinds[0].slice(1) : 'Pets';
  const maxPets = num(p.property_max_pets);
  const weight = num(p.property_max_pet_weight_limit);
  const fee = num(p.property_upfront_pet_fee);
  const dep = num(p.property_pet_deposit);
  const rent = num(p.property_monthly_pet_rent);
  return [
    ['Welcome', welcome, ''],
    ['Up to', maxPets ? `${maxPets} pet${maxPets === 1 ? '' : 's'}` : 'Ask the leasing team', weight ? `under ${weight} lb` : ''],
    ['One-time fee', fee ? `From ${money(fee)}` : 'Ask the leasing team', fee ? `per pet${dep ? ` + ${money(dep)} deposit` : ''}` : ''],
    ['Pet rent', rent ? money(rent)! : 'Ask the leasing team', rent ? 'per pet, monthly' : ''],
  ];
}

/** "Dogs & cats · up to 2 · $35/mo per pet" */
export function petSummary(p: Record<string, unknown>): string {
  const [welcome, upTo, , rent] = petCells(p);
  const rentN = num(p.property_monthly_pet_rent);
  return [welcome[1], num(p.property_max_pets) ? `up to ${num(p.property_max_pets)}` : '', rentN ? `${rent[1]}/mo per pet` : ''].filter(Boolean).join(' · ') || upTo[1];
}
